import { isAfter, isEqual } from 'date-fns';

import { HOTEL_RESULTS_ROUTE } from '@hotels/hotel-results/constants/routes';
import { ReservationType, UserHistoryItem } from '@user-history/entities/user-history-item';
import { environment as ENV } from '@environment';
import { PaymentReservationStatus, ReservationStatus } from '@user-history/entities/reservation-status';
import { CrossSellingAction, CrossSellingImage, CrossSellingTranslations } from '@shared/cross-selling/entities/cross-selling';
import { UserHistoryOptions } from '@user-history/entities/user-history-options';
import { HistoryBookingDetail } from '@user-history/entities/history-booking-detail';
import { AssetCrossSelling } from '@asset-cross-selling';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { Travelers } from '@booking/entities/booking-arrangement';
import { PaxType } from '@booking/entities/pax-type';
import { isCancelledReservationByStatus, isConfirmReservationByStatus } from '@ux/mappers/reservation-status.mapper';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';

const IMAGES_BASE_PATH = 'web/travel/CrossSelling';

const UNTIL = 'USER_HISTORY_MODULE.CROSS_SELLING.UNTIL';
const TRAVEL_EASY = 'USER_HISTORY_MODULE.CROSS_SELLING.TRAVEL_EASY';

const HOTEL_BY_FLIGHT_OFFER = 'USER_HISTORY_MODULE.CROSS_SELLING.HOTEL_BY_FLIGHT_OFFER';
const SEE_HOTELS = 'USER_HISTORY_MODULE.CROSS_SELLING.SEE_HOTELS';
const CROSS_SELLING_HOTEL_IMAGE = 'cross-selling-hotel-banner.png';

const ASSISTANCE_OFFER = 'USER_HISTORY_MODULE.CROSS_SELLING.BUY_ASSISTANCE';
const I_WANT_IT = 'USER_HISTORY_MODULE.CROSS_SELLING.I_WANT_IT';
const PERCENTAGE_OFFER = 'USER_HISTORY_MODULE.CROSS_SELLING.DISCOUNT_HOTEL_PERCENT';
const CROSS_SELLING_ASSISTANCE_IMAGE = 'cross-selling-assistance.png';
const CROSS_SELLING_ASSISTANCE_LOGO_IMAGE = 'assistance-chubb-logo.png';

export function getCrossSellingTranslates(type: ReservationType): CrossSellingTranslations {
  switch (type) {
    case ReservationType.Hotel:
      return {
        Title: UNTIL,
        Offer: HOTEL_BY_FLIGHT_OFFER,
        PercentageOffer: PERCENTAGE_OFFER,
        SeeOffer: SEE_HOTELS
      };
    case ReservationType.Assistance:
      return {
        Title: TRAVEL_EASY,
        Offer: ASSISTANCE_OFFER,
        PercentageOffer: null,
        SeeOffer: I_WANT_IT
      };
    default:
      return null;
  }
}

export function getCrossSellingImage(type: ReservationType): CrossSellingImage {
  switch (type) {
    case ReservationType.Hotel:
      return {
        Path: IMAGES_BASE_PATH,
        ImageUrl: CROSS_SELLING_HOTEL_IMAGE,
        LogoUrl: null,
      };
    case ReservationType.Assistance:
      return {
        Path: IMAGES_BASE_PATH,
        ImageUrl: CROSS_SELLING_ASSISTANCE_IMAGE,
        LogoUrl: CROSS_SELLING_ASSISTANCE_LOGO_IMAGE
      };
    default:
      return null;
  }
}

export function getCrossSellingUrlParams(
  type: ReservationType, startDate: Date, endDate: Date, destination: string, passengers: Travelers[] = []
): string {
  switch (type) {
    case ReservationType.Hotel:
      return `/${HOTEL_RESULTS_ROUTE}/${HotelDestinationInputType.SearchBar}/${destination}/${formatDate(startDate)}` +
        `/${formatDate(endDate)}/${ENV.countryInfo.currency_code}/${guestQueryParam(passengers, startDate)}`;
    default:
      return '';
  }
}

export function getCrossSellingAction(type: ReservationType): CrossSellingAction {
  switch (type) {
    case ReservationType.Hotel:
      return CrossSellingAction.NAVIGATE;
    case ReservationType.Assistance:
      return CrossSellingAction.EMIT_TYPE;
    default:
      return null;
  }
}

export function getCrossSellingSource(type: ReservationType): EVENT_SOURCE {
  switch (type) {
    case ReservationType.Hotel:
      return EVENT_SOURCE.CROSS_SELLING_HOTEL;
    case ReservationType.Assistance:
      return EVENT_SOURCE.CROSS_SELLING_ASSISTANCE;
    default:
      return null;
  }
}

export function isValidGeneralCrossSellingByDetail(detail: HistoryBookingDetail): boolean {
  const isConfirmReservation = isConfirmByCrossSelling(detail?.Status as ReservationStatus);
  const isPaymentReservation = isPaymentByCrossSelling(detail.PaymentStatus);
  return (!!isConfirmReservation && !!isPaymentReservation);
}

export function isValidGeneralCrossSellingByItem(item: UserHistoryItem, tab: UserHistoryOptions): boolean {
  const reservation = item?.ReservationItems?.[0];
  const isConfirmReservation = isConfirmByCrossSelling(reservation.ReservationStatus);
  const isPaymentReservation = isPaymentByCrossSelling(item.PaymentStatus as PaymentReservationStatus);
  const isUpcomingView = isUpcomingTab(tab);
  return (!!isConfirmReservation && !!isPaymentReservation && !!isUpcomingView);
}

export function haveCompatibleReservationsByCrossSelling(
  startDate: Date, endDate: Date, destination: string, history: UserHistoryItem[], filterType: ReservationType
): boolean {
  if (history.length) {
    return history
      .filter(item => {
        const isPaymentReservation = isPaymentByCrossSelling(item.PaymentStatus as PaymentReservationStatus);
        return item?.ReservationItems?.[0].ReservationItemType === filterType &&
          isPaymentReservation && (
          !isCancelledReservationByStatus(item?.ReservationItems?.[0]?.ReservationStatus) &&
          item?.ReservationItems?.[0]?.ReservationStatus !== ReservationStatus.Expired);
      })
      .some(item => {
        const today = new Date();
        const itemStartDate = new Date(item?.ReservationItems?.[0].StartDate);
        const itemEndDate = new Date(item?.ReservationItems?.[0].EndDate);
        today.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (isAfter(startDate, today)) {
          itemStartDate.setHours(0, 0, 0, 0);
          itemEndDate.setHours(0, 0, 0, 0);
          const isEqualsDates = isEqual(itemStartDate, startDate) && isEqual(itemEndDate, endDate);
          return (destination === item?.ReservationItems?.[0]?.Destination) && isEqualsDates;
        } else return true;
      });
  } else return true;
}

export function isActiveAssistanceCrossSellingDetailByConfig(type: ReservationType): boolean {
  return !!AssetCrossSelling?.ASSISTANCE_DETAIL?.includes(type);
}

export function isActiveAssistanceCrossSellingListByConfig(type: ReservationType): boolean {
  return !!AssetCrossSelling?.ASSISTANCE_LIST?.includes(type);
}

export function isActiveProductListCrossSellingByConfig(type: ReservationType): boolean {
  return !!AssetCrossSelling?.PRODUCT_LIST?.includes(type);
}

export function isActiveProductDetailCrossSellingByConfig(type: ReservationType): boolean {
  return !!AssetCrossSelling?.PRODUCT_DETAIL?.includes(type);
}

function guestQueryParam(passengers: Travelers[], startDate: Date): string {
  if (passengers.length === 0) {
    return '1_0_';
  } else {
    const adults = getPassengersByType(PaxType.ADULTS, passengers);
    const children = getPassengersByType(PaxType.CHILDREN, passengers);
    const infants = getPassengersByType(PaxType.INFANTS, passengers);
    const _children = children + infants;
    const ages = _children ? passengers
      .filter(passenger => passenger.PaxType !== PaxType.ADULTS)
      .map(passenger => getAgeByCrossSelling(passenger.DOB, startDate))
      .join('_') : '';
    return `${adults}_${_children}_${ages}`;
  }
}

function getPassengersByType(type: string, passengers: Travelers[]): number {
  return passengers.filter(passenger => passenger.PaxType === type).length;
}

function getAgeByCrossSelling(birthdate: string, startDate: Date): number {
  const MS_IN_ONE_DAY = 24 * 60 * 60 * 1000;
  const DAYS_IN_YEAR = 365.25;
  return Math.floor(diffTime(birthdate, startDate) / (MS_IN_ONE_DAY * DAYS_IN_YEAR));
}

function diffTime(birthdate: string, startDate: Date): number {
  const birthDate = new Date(birthdate);
  startDate.setHours(0, 0, 0, 0);
  birthDate.setHours(0, 0, 0, 0);
  return startDate.getTime() - birthDate.getTime();
}

function formatDate(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0);
  let month = `${(d.getUTCMonth() + 1)}`;
  let day = `${d.getUTCDate()}`;
  const year = `${d.getUTCFullYear()}`;
  if (month.length < 2) {
    month = `0${month}`;
  }
  if (day.length < 2) {
    day = `0${day}`;
  }
  return [year, month, day].join('-');
}

function isUpcomingTab(tab: UserHistoryOptions): boolean {
  return tab === UserHistoryOptions.UPCOMING;
}

function isConfirmByCrossSelling(status: ReservationStatus): boolean {
  return isConfirmReservationByStatus(status);
}

function isPaymentByCrossSelling(paymentStatus: PaymentReservationStatus): boolean {
  return paymentStatus === PaymentReservationStatus.Approved;
}
